// astro vue will render empty page if import vue from 'vue'
// @ts-ignore
import { createApp } from 'vue/dist/vue.esm-bundler.js'
import { computed, ref, reactive } from 'vue'
import { useStore } from '@nanostores/vue'
import { isLogin } from '@/store/userStore'
import BaseButton from '@/components/ui/button/BaseButton.vue'
import type { StepData } from '@/types/StepCheckout'
import type { WarehouseCardData } from '@/types/WareHouse'
import { getProductSharingUrl } from '@/legacy/ts/productSharing'
import { type RegistrationCardData } from '@/components/RegistrationCard.vue'
import { cn, getCreditText, formatSrc } from '@/lib/utils'
import { useTooltip } from '@/lib/useTooltip'
import { type HeroSectionData } from '@/components/HeroSection.vue'
import { isApp, isAppLogin } from '@/assets/ts/checkApp'
import hybridPage from '@/lib/hybridPage'
import { fetchBrands } from '@/assets/ts/apiManagement'
import type { IBrand, ICountry } from '@/types/Brands'
import { createGAEvent, GaGeneralPageName } from '@/assets/ts/customEvent'
import { getSiteBaseMemberPath, getSiteBaseUrl } from '@/lib/siteBaseUrl'
import getRedirectSeoLink from '@/lib/getSeoRedirect'
import IconArrowGoto from '@/assets/icons/arrow-goto.svg'
import { isGlobalSite } from '@/lib/checkService'
import ShippingFaq from '@/pages/shipment-protection-shipping/components/_ShipmentProtectionFAQ.vue'
import PageBreadCrumb from '@/components/PageBreadCrumb.vue'

hybridPage()

createApp({
	name: 'Proxy',
	components: {
		BaseButton,
		ShippingFaq,
		PageBreadCrumb,
	},
	setup() {
		const pageName = GaGeneralPageName.ProxyShopping
		createGAEvent('page_load', {
			page_name: pageName,
		})
		const isLoginGlobal = useStore(isLogin)
		const isInGlobalSite = isGlobalSite()

		// ================== hero section start ==================
		const heroSectionData: HeroSectionData = window.pageContent.heroContent
		const currentHeroSectionData = computed(() => {
			return isLoginGlobal.value
				? heroSectionData.afterLogin
				: heroSectionData.beforeLogin
		})

		const { buyforyouWedgetText } = window.pageContent

		function newRequest() {
			if (isApp) {
				appAction()
				return
			}
			if (isLoginGlobal.value) {
				window.location.href = `${buyforyouWedgetText.newHref}`
			} else {
				if (
					window.BUYANDSHIP.settings &&
					window.BUYANDSHIP.settings.member_base_path
				) {
					let redirectVal = `${getSiteBaseMemberPath()}/buyforyou/new`
					if (redirectVal.indexOf('/') === 0) {
						redirectVal = redirectVal.substring(1)
					}
					localStorage.setItem('loginRedirectValue', redirectVal)
				}
				window.BsHeaderVm.showPanel('login')
			}
		}

		function appAction() {
			const link = `${buyforyouWedgetText.newHref}`
			if (isAppLogin) window.location.href = link
			else window.location.href = `${buyforyouWedgetText.loginHref}`
		}

		function handleHeroSectionButtonClick() {
			createGAEvent('button_click', {
				page_name: pageName,
				button_name: 'Place Order',
			})
			newRequest()
		}

		// ================== Flow ==================
		const Flowdata: StepData[] = window.pageContent.flowContent.content
		const { containerRef } = useTooltip(Flowdata)

		const DEFAULTITEM = 0
		const openIndex = ref(DEFAULTITEM)

		const isActiveStep = (index: number) => openIndex.value === index
		const summaryClasses = (index: number) =>
			cn(
				'p-20px flex flex-col gap-8px',
				isActiveStep(index)
					? 'bg-t-900'
					: 'bg-transparent hover:bg-b-5% active:bg-b-5%',
				isActiveStep(index)
					? 'rounded-md'
					: 'rounded-none hover:rounded-md active:rounded-md',
				isActiveStep(index) ? ' border-0' : ' border-b-n-200 border-b-2',
			)
		const stepClasses = (index: number) =>
			cn(
				'typography-s1-emp',
				isActiveStep(index) ? 'text-t-400' : 'text-t-600',
				'hover:bg-gray-200',
			)
		const titleClasses = (index: number) =>
			cn('typography-h5-str', isActiveStep(index) ? 'text-white' : 'text-black')

		/**
		 * Function to control the expansion or collapse of steps.
		 * Clicking an already expanded step does nothing, ensuring one step is always open.
		 */
		const toggleDetail = (index: number): void => {
			// If a non-current step is clicked, switch to that step
			if (openIndex.value !== index) {
				openIndex.value = index
			}
			// If the current expanded step is clicked, do nothing (ensuring at least one step is open)
		}
		// ================== WareHouse ==================
		const warehouseCardData: WarehouseCardData =
			window.pageContent.warehouseContent
		const currenWarehouseContent = computed(() => {
			return isLoginGlobal.value
				? warehouseCardData.afterLogin
				: warehouseCardData.beforeLogin
		})
		const isCurrentRegionJp = computed(() => {
			return window.BUYANDSHIP.settings?.current_country === 'jp'
		})

		const handleClickWarehouse = (link?: string) => {
			if (link) {
				window.location.href = link
			} else {
				const url = `${import.meta.env.PUBLIC_ROOT_URL}/${getRedirectSeoLink(
					'warehouses',
				)}`
				window.location.href = url
			}
		}

		// ---------------------
		// Service Content Section
		// ---------------------
		const currentServiceContent = window.pageContent.serviceContent

		// ---------------------
		// Registration Card Section Start
		// ---------------------
		const registrationCardData: RegistrationCardData =
			window.pageContent.registrationContent

		const currentRegistrationCardContent = computed(() => {
			return isLoginGlobal.value
				? registrationCardData.afterLogin
				: registrationCardData.beforeLogin
		})

		const currentRegistrationCardTitle1 = computed(() => {
			return getCreditText(currentRegistrationCardContent.value.title1)
		})

		const currentRegistrationCardTitle2 = computed(() => {
			return getCreditText(currentRegistrationCardContent.value.title2)
		})

		const currentRegistrationCardDesc = computed(() => {
			return getCreditText(currentRegistrationCardContent.value.description)
		})

		const WELCOME_CREDIT = window.BUYANDSHIP?.settings?.member_welcome_credits

		const bgRegistrationCardClass = computed(() => {
			return currentRegistrationCardContent.value.class
		})

		const openReferral = (link?: string) => {
			if (link) {
				window.location.href = link
				return
			}
			window.location.href = getProductSharingUrl('referral')
		}

		const openRegister = () => {
			window.BsHeaderVm.showSignupPanel()
		}

		const handleRegistrationCardClick = (link?: string) => {
			if (isLoginGlobal.value) {
				openReferral(link)
			} else {
				openRegister()
			}
		}
		// ---------------------
		// Registration Card Section End
		// ---------------------

		const contentOtherService = window.pageContent.otherServiceContent

		const stateBrands = reactive<{
			isLoading: boolean
			brands: IBrand[]
			countries: ICountry[]
			currentCountry: string
		}>({
			isLoading: true,
			brands: [],
			countries: [],
			currentCountry: '',
		})

		const currentBrandList = computed(() => {
			const maxBrands = 12
			const temp = stateBrands.brands.filter(
				brand => brand.country_id === stateBrands.currentCountry,
			)
			temp.forEach(brand => {
				brand.icon = IconArrowGoto
			})
			return temp.length > maxBrands ? temp.slice(0, maxBrands) : temp
		})

		async function fetchBrandsData() {
			try {
				const { content } = await fetchBrands()
				const maxCountries = 6
				stateBrands.brands = content.brands
				stateBrands.countries =
					content.countries.length > maxCountries
						? content.countries.slice(0, maxCountries)
						: content.countries
				stateBrands.currentCountry = content.countries[0].id
				stateBrands.isLoading = false
			} catch (error) {
				console.log(error)
			}
		}

		function clickCountryTab(id: string) {
			if (id) {
				stateBrands.currentCountry = id
			}
		}

		fetchBrandsData()

		// ---------------------
		// Remark Section
		// ---------------------
		const currentRemarkSection = window.pageContent.remarkSectionContent

		// ---------------------
		// FAQ Section
		// ---------------------
		const faqContent = computed(() => {
			return window.pageContent.faq
		})
		const breadcrumbs = computed(() => [
			{
				displayName: window.pageContent.breadcrumbs?.home || '',
				categoryId: 'home',
				link: getSiteBaseUrl(),
			},
			{
				displayName: window.pageContent.breadcrumbs?.shopping || '',
				categoryId: 'shopping',
				link: `${getSiteBaseUrl()}/${getRedirectSeoLink('proxyShopping')}/`,
			},
		])

		return {
			WELCOME_CREDIT,
			isLoginGlobal,
			cn,
			formatSrc,
			// ================== WareHouse ==================
			currenWarehouseContent,
			handleClickWarehouse,
			isCurrentRegionJp,
			// ================== Service ==================
			currentServiceContent,
			// ================== Flow ==================
			containerRef,
			Flowdata,
			summaryClasses,
			stepClasses,
			titleClasses,
			toggleDetail,
			openIndex,
			// ================== Hero ==================
			currentHeroSectionData,
			handleHeroSectionButtonClick,
			// ================== Registration Card ==================
			currentRegistrationCardContent,
			currentRegistrationCardTitle1,
			currentRegistrationCardTitle2,
			currentRegistrationCardDesc,
			bgRegistrationCardClass,
			handleRegistrationCardClick,
			// ================== Other Service ==================
			contentOtherService,
			stateBrands,
			currentBrandList,
			clickCountryTab,
			isInGlobalSite,
			// ================== Remark Service ==================
			currentRemarkSection,
			// ================== FAQ ==================
			faqContent,
			breadcrumbs,
		}
	},
}).mount('#bsMain')
